import { useCallback } from "react";
import { Button } from "@aidkitorg/component-library";
import { StatusFilter } from "./StatusFilter";
import { ChannelFilter } from "./ChannelFilter";
import { useLocalizedStrings } from "../../../../Localization";
import type { NotificationDetails } from "@aidkitorg/types/lib/survey";

export interface NotificationRecipientsFilterParams {
  statuses?: string[];
  channels?: string[];
}

interface NotificationRecipientsFiltersProps {
  onFiltersChange: (filters: NotificationRecipientsFilterParams) => void;
  availableStatuses: string[];
  availableChannels: string[];
  defaultTableColumns?: NotificationDetails['defaultTableColumns'];
  showStatus?: boolean;
  setActiveFilters?: React.Dispatch<React.SetStateAction<number>>;
  filtersState: NotificationRecipientsFilterParams;
  setFiltersState: React.Dispatch<React.SetStateAction<NotificationRecipientsFilterParams>>;
}

export const NotificationRecipientsFilters: React.FC<NotificationRecipientsFiltersProps> = ({
  onFiltersChange,
  availableStatuses,
  availableChannels,
  defaultTableColumns,
  showStatus = true,
  setActiveFilters,
  filtersState,
  setFiltersState,
}) => {
  const L = useLocalizedStrings();

  // Determine which filters to show based on config
  const showStatusFilter = showStatus && defaultTableColumns?.status === 'visibleAndFilterable';
  const showChannelsFilter = defaultTableColumns?.channel === 'visibleAndFilterable';

  // Filter handlers
  const handleStatusesChange = useCallback((statuses: string[]) => {
    setFiltersState(f => ({ ...f, statuses }));
  }, [setFiltersState]);

  const handleChannelsChange = useCallback((channels: string[]) => {
    setFiltersState(f => ({ ...f, channels }));
  }, [setFiltersState]);

  // Apply filters function
  const applyFilters = useCallback(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filtersState).filter(([_, value]) =>
        Array.isArray(value) ? value.length > 0 : true
      )
    ) as NotificationRecipientsFilterParams;

    // Count active filters
    let activeFilterCount = 0;
    if (filtersState.statuses && filtersState.statuses.length > 0) activeFilterCount++;
    if (filtersState.channels && filtersState.channels.length > 0) activeFilterCount++;

    // Update active filters count if setter is provided
    if (setActiveFilters) {
      setActiveFilters(activeFilterCount);
    }

    onFiltersChange(cleanFilters);
  }, [filtersState, onFiltersChange, setActiveFilters]);

  // Reset filters function
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      statuses: [],
      channels: [],
    };
    setFiltersState(emptyFilters);

    // Reset active filters count if setter is provided
    if (setActiveFilters) {
      setActiveFilters(0);
    }

    onFiltersChange({});
  }, [onFiltersChange, setActiveFilters, setFiltersState]);

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showStatusFilter && (
          <StatusFilter
            selectedStatuses={filtersState.statuses || []}
            availableStatuses={availableStatuses}
            onStatusesChange={handleStatusesChange}
          />
        )}
        {showChannelsFilter && (
          <ChannelFilter
            selectedChannels={filtersState.channels || []}
            availableChannels={availableChannels}
            onChannelsChange={handleChannelsChange}
          />
        )}
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>
            {L.admin_panel.notifications.reset_filters}
          </Button>
          <Button onClick={applyFilters}>
            {L.admin_panel.notifications.apply_filters}
          </Button>
        </div>
      </div>
    </div>
  );
};
